import { cx } from "@/utils";

import styles from "./Typography.module.css";

export type Variant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle-1"
  | "subtitle-2"
  | "body-1"
  | "body-2"
  | "caption"
  | "monospace"
  | "small"
  | "error";

const DEFAULT_COMPONENT_MAPPING: Record<Variant, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  "subtitle-1": "p",
  "subtitle-2": "p",
  "body-1": "p",
  "body-2": "p",
  monospace: "p",
  caption: "span",
  small: "small",
  error: "p",
};

export type TypographyProps = {
  variant?: Variant;
  block?: boolean;
  margin?: "none" | "default";
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  children?: React.ReactNode;
  component?: React.ElementType;
  className?: string;
  align?: "inherit" | "left" | "right" | "center";
  wordBreak?: boolean;
  typographyRef?: React.RefObject<HTMLElement>;
} & Omit<React.HTMLProps<HTMLSpanElement>, "children">;

const Typography: React.FunctionComponent<TypographyProps> = ({
  component,
  variant = "body-1",
  children,
  className,
  align = "left",
  block,
  wordBreak,
  typographyRef,
  margin,
  ...rest
}) => {
  const Component = component || DEFAULT_COMPONENT_MAPPING[variant];

  return (
    <Component
      ref={typographyRef}
      className={cx(
        styles.root,
        styles[variant],
        styles[`align-${align}`],
        {
          [styles.block]: block,
          [styles.wordBreak]: wordBreak,
          [styles.marginsNone]: margin === "none",
        },
        className
      )}
      {...rest}
    >
      {children}
    </Component>
  );
};

export default Typography;
